/**
 * shared/types/couple.ts — Couple and member model, reply colours, pairing results, and the exit/re-pair bundle.
 *
 * Moved verbatim out of the former shared/types.ts monolith; the public
 * surface is unchanged and shared/types/index.ts re-exports it.
 */
/* ------------------------------------------------------------------ *
 * 2. Couple / member model
 * ------------------------------------------------------------------ */

/** Partner A creates the couple; Partner B joins. Drives the two-ness UI. */
export type Role = 'A' | 'B';

export interface Member {
  memberId: string;      // uuid v4, assigned by the DO on join
  role: Role;
  displayName: string;   // free text, may be ''
  /** The colour this member picked for their reply text (card 9QADJv3CdiOh):
   *  one of REPLY_COLOUR_KEYS, or absent/null when they never chose. The
   *  partner's Thread reads it off this list — same source as displayName,
   *  so a change shows live on both sockets. Optional because the pair-create
   *  path builds a Member straight after the INSERT, before any pick exists. */
  replyColour?: string | null;
  joinedAt: number;      // epoch ms
}

/**
 * Reply-text colours (card 9QADJv3CdiOh, founder email 2026-10-23): the sender
 * picks one in Settings > You and their words wear it in the partner's feed.
 *
 * The text lands on the partner bubble's mustard ground (#D1A23B), so every
 * entry is a DARK ink measured >= 4.5:1 against mustard — AA for body text.
 * The list is closed on purpose: the DO validates against these keys, so a
 * client cannot post an arbitrary hex that fails contrast. The picker, the
 * server's reason and the Thread fallback all read this one list.
 */
export interface ReplyColour {
  key: string;
  label: string;
  /** The measured ink. Contrast on #D1A23B noted per entry (all >= 4.5:1). */
  hex: string;
}

export const REPLY_COLOURS: readonly ReplyColour[] = [
  { key: 'bottle-green', label: 'Bottle green', hex: '#1F3B2D' }, // 5.19:1 — the current ink, kept as the default
  { key: 'ink',          label: 'Ink',          hex: '#111827' }, // 7.55:1
  { key: 'espresso',     label: 'Espresso',     hex: '#3E2723' }, // 5.89:1
  { key: 'wine',         label: 'Wine',         hex: '#4C1D2E' }, // 5.87:1
  { key: 'navy',         label: 'Navy',         hex: '#1C2E5C' }, // 5.61:1
  { key: 'midnight',     label: 'Midnight',     hex: '#16324F' }, // 5.58:1
  { key: 'charcoal',     label: 'Charcoal',     hex: '#2F3136' }, // 5.54:1
  { key: 'oxblood',      label: 'Oxblood',      hex: '#5C1A1B' }, // 5.52:1
  { key: 'pine',         label: 'Pine',         hex: '#1A3A2A' }, // 5.31:1
  { key: 'chocolate',    label: 'Chocolate',    hex: '#4A2E19' }, // 5.27:1
  { key: 'plum',         label: 'Plum',         hex: '#5B1F3D' }, // 5.23:1
  { key: 'deep-teal',    label: 'Deep teal',    hex: '#0B3D3D' }, // 5.11:1
  { key: 'denim',        label: 'Denim',        hex: '#1E3A5F' }, // 4.90:1
  { key: 'indigo',       label: 'Indigo',       hex: '#312E81' }, // 4.86:1
  { key: 'maroon',       label: 'Maroon',       hex: '#701A1A' }, // 4.83:1
  { key: 'burgundy',     label: 'Burgundy',     hex: '#6E1E2C' }, // 4.74:1
] as const;

export const REPLY_COLOUR_KEYS: readonly string[] = REPLY_COLOURS.map((c) => c.key);

export function isReplyColourKey(value: unknown): value is string {
  return typeof value === 'string' && (REPLY_COLOUR_KEYS as readonly string[]).includes(value);
}

/** The hex for a stored key, or null when the key is unknown (old row, bad
 *  write) — the client falls back to the role token rather than painting
 *  something unmeasured. */
export function replyColourHex(key: string | null | undefined): string | null {
  if (!key) return null;
  return REPLY_COLOURS.find((c) => c.key === key)?.hex ?? null;
}

export interface Couple {
  coupleId: string;       // coupleIdFromCode(code)
  code: string;           // the 6-char invite code
  createdAt: number;      // epoch ms
  expiresAt: number;      // createdAt + INVITE_TTL_MS — invite expiry, not couple expiry
  pairedAt: number | null; // epoch ms when the 2nd member joined, null while open
  /** Card Vf23eSKKxDhX — the tombstone: epoch ms when the LAST member left.
   *  Null while the room is open. A closed couple keeps every row it ever
   *  held (nothing is deleted on leave) but refuses join and refreshInvite,
   *  so its old invite code can never re-admit anyone. */
  closedAt: number | null;
  members: Member[];      // length 1 (open) or 2 (paired). NEVER > 2.
}

/**
 * The milestone line the ceremonial pairing moment shows AND the memory it
 * files in the Vault (card 2NuQ8dd2JMqs) — "You're in 🍐 · Oct 2026".
 * Computed from the clock rather than stored: it is stamped at the moment the
 * couple completes, so it is right for every couple and needs no schema.
 * The web bundle keeps its own copy (web/src/components/PairCeremony.tsx),
 * same convention as INVITE_ALPHABET.
 */
export function pairMilestone(now: Date = new Date()): string {
  const month = now.toLocaleDateString('en-AU', { month: 'short' });
  return `You're in 🍐 · ${month} ${now.getFullYear()}`;
}

/** Result of a successful join — returned by POST /api/pair/join. */
export interface JoinResult {
  couple: Couple;
  memberId: Member['memberId'];
  role: Role;
  token: string; // see §3
  /** Ceremonial pairing moment (card 2NuQ8dd2JMqs). True ONLY on the join
   *  that completed the couple — the second member's own response is what
   *  tells that device to celebrate, because the room's `paired` broadcast
   *  fires before this response and reaches only sockets already attached
   *  (the joiner has no socket yet). Never set on create or on a re-read. */
  justPaired?: boolean;
}

/**
 * Card Vf23eSKKxDhX — what a leave left behind.
 *
 * `members` is who is still in the room (length 1 = the survivor can re-invite,
 * length 0 = the room took the tombstone). `closed` mirrors that in one word
 * so the route does not have to count, and `couple` is the room as it now
 * stands — the survivor's client re-reads it straight from this response
 * rather than paying a second /api/pair/status round trip.
 */
export interface RemoveMemberResult {
  members: Member[];
  closed: boolean;
  couple: Couple;
  /** How many of the leaver's inbound open requests were closed by the exit
   *  (card uTudCJtABptq). 0 when none were open. */
  closedRequests: number;
  /** Display name of the member who just left (card ZyfsefNk8p3J), captured
   *  before their row was deleted so the survivor's line can name them. Null
   *  on the degenerate no-member-pointer path. */
  leaverName: string | null;
}

/* ---------------------------------------------------------- *
 *  Exit / re-pair carry-over (cards ocJz1TeLT4pi, O2J0Cm1ZFeNd)
 * ---------------------------------------------------------- */

/**
 * One member's private half of a room, as raw DB rows grouped by table.
 * The EXIT endpoint serialises exactly what the member owns and nothing the
 * couple shares. IMPORT re-files these rows into a *new* room under the new
 * memberId, regenerating the id columns so a second import cannot collide.
 *
 * Parsed, validated, never trusted: each table's row shape is checked on
 * import before a single INSERT runs, and the row just crosses the room
 * boundary with its timestamps preserved.
 */
export interface ExitBundle {
  /** Bump when the row shapes below change. Import refuses an unknown version. */
  version: 1;
  /** When the bundle was written (ms). Import preserves this for audit. */
  exportedAt: number;
  /** The member who owns every row in this bundle. */
  ownerMemberId: string;
  /** Private notebook items: shared=0 AND added_by = owner. */
  notebook: Record<string, unknown>[];
  /** Inbox items on the 'me' lane owned by the member. */
  inbox: Record<string, unknown>[];
  /** gift_private_lists rows with member_id = owner. */
  giftPrivate: Record<string, unknown>[];
  /** gift_preferences row for the owner, if any. */
  giftPreference: Record<string, unknown> | null;
  /** member_interests row for the owner, if any. */
  interests: Record<string, unknown> | null;
  /** member_profile birthday for the owner, if any. */
  birthday: string | null;
  /** question_answers rows authored by the owner. */
  questionAnswers: Record<string, unknown>[];
  /** requests SENT by the owner (from_member_id = owner). */
  requests: Record<string, unknown>[];
}
